import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import useMetaTags from '../../hooks/useMetaTags';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Stack,
  Chip,
  Divider,
  IconButton,
  Paper,
  Button,
  ThemeProvider,
  createTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PageHero from '../../components/PageHero/PageHero';
import Footer from '../../components/Footer/Footer';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import ShareIcon from '@mui/icons-material/Share';
import baseTheme from '../../theme/theme';
import {
  fetchWordPressPostBySlug,
  transformWordPressPostToBlogPost,
  BlogPost,
  stripHtml
} from '../../services/wordpressApi';
import { handleShare as shareUtil, isMobileDevice, isNativeShareSupported } from '../../utils/shareUtils';

// Create a custom theme for blog details
const blogDetailTheme = createTheme({
  ...baseTheme,
  palette: {
    ...baseTheme.palette,
    primary: {
      main: '#455b40',
      light: '#5a7654',
      dark: '#34442f',
      contrastText: '#FFFFFF'
    }
  }
});

// Styled Components
const BlogDetailContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#f8f9fa',
  minHeight: '100vh',
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(8)
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '400px'
}));

const BackButton = styled(Button)(({ theme }) => ({
  color: '#455b41',
  textTransform: 'none',
  fontWeight: 600,
  marginBottom: theme.spacing(3),
  '&:hover': {
    backgroundColor: 'rgba(69, 91, 65, 0.05)'
  }
}));

const BlogHeader = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(6),
  borderRadius: '20px',
  marginBottom: theme.spacing(4),
  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
  border: '1px solid rgba(69, 91, 65, 0.1)'
}));

const BlogTitle = styled(Typography)(({ theme }) => ({
  fontFamily: '"Lora", serif',
  fontWeight: 700,
  fontSize: '2.5rem',
  color: '#2a3a28',
  lineHeight: 1.2,
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down('md')]: {
    fontSize: '2rem'
  }
}));

const BlogMeta = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: theme.spacing(3),
  alignItems: 'center',
  marginBottom: theme.spacing(2)
}));

const MetaItem = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
  '& svg': {
    fontSize: '18px',
    color: '#455b41'
  }
}));


const ContentContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(6),
  borderRadius: '20px',
  marginBottom: theme.spacing(4),
  background: '#ffffff',
  border: '1px solid rgba(69, 91, 65, 0.05)'
}));

const BlogContent = styled(Box)(({ theme }) => ({
  fontSize: '1.125rem',
  lineHeight: 1.8,
  color: '#2c2c2c',
  fontFamily: '"Lora", serif',
  '& p': {
    marginBottom: theme.spacing(3),
    fontSize: '1.125rem',
    lineHeight: 1.8
  },
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    fontFamily: '"Lora", serif',
    fontWeight: 700,
    color: '#2a3a28',
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
    lineHeight: 1.3
  },
  '& h1': { fontSize: '2.5rem' },
  '& h2': { fontSize: '2rem' },
  '& h3': { fontSize: '1.5rem' },
  '& h4': { fontSize: '1.25rem' },
  '& ul, & ol': {
    marginBottom: theme.spacing(3),
    paddingLeft: theme.spacing(3)
  },
  '& li': {
    marginBottom: theme.spacing(1),
    lineHeight: 1.7
  },
  '& img': {
    maxWidth: '100%',
    height: 'auto',
    borderRadius: '12px',
    margin: `${theme.spacing(3)} auto`,
    display: 'block',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
  },
  '& figure': {
    margin: `${theme.spacing(4)} 0`,
    textAlign: 'center',
    '& img': {
      borderRadius: '12px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
    },
    '& figcaption': {
      marginTop: theme.spacing(1),
      fontSize: '0.9rem',
      color: '#666',
      fontStyle: 'italic'
    }
  },
  '& iframe': {
    width: '100%',
    height: '450px',
    borderRadius: '12px',
    margin: `${theme.spacing(4)} 0`,
    display: 'block',
    border: 'none',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
    [theme.breakpoints.down('md')]: {
      height: '350px'
    },
    [theme.breakpoints.down('sm')]: {
      height: '250px'
    }
  },
  '& .wp-block-embed': {
    textAlign: 'center',
    margin: `${theme.spacing(4)} 0`,
    '& iframe': {
      maxWidth: '100%',
      width: '100%',
      height: '450px',
      [theme.breakpoints.down('md')]: {
        height: '350px'
      },
      [theme.breakpoints.down('sm')]: {
        height: '250px'
      }
    }
  },
  '& blockquote': {
    borderLeft: `4px solid #455b41`,
    paddingLeft: theme.spacing(3),
    marginLeft: 0,
    marginRight: 0,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    fontStyle: 'italic',
    fontSize: '1.2rem',
    color: '#455b41',
    backgroundColor: 'rgba(69, 91, 65, 0.05)',
    padding: theme.spacing(3),
    borderRadius: '8px'
  },
  '& a': {
    color: '#455b41',
    textDecoration: 'none',
    borderBottom: '2px solid rgba(69, 91, 65, 0.3)',
    transition: 'all 0.3s ease',
    '&:hover': {
      borderBottomColor: '#455b41',
      backgroundColor: 'rgba(69, 91, 65, 0.05)'
    }
  },
  '& .wp-block-spacer': {
    margin: `${theme.spacing(2)} 0`
  },
  '& .wp-block-uagb-container': {
    marginBottom: theme.spacing(3)
  }
}));

const TagsContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  marginTop: theme.spacing(4)
}));

const TagChip = styled(Chip)(({ theme }) => ({
  backgroundColor: 'rgba(69, 91, 65, 0.1)',
  color: '#455b41',
  fontWeight: 600,
  '&:hover': {
    backgroundColor: 'rgba(69, 91, 65, 0.2)'
  }
}));

const ShareContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginTop: theme.spacing(4),
  padding: theme.spacing(3),
  backgroundColor: 'rgba(69, 91, 65, 0.05)',
  borderRadius: '12px'
}));

const ShareButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: '#455b41',
  color: '#ffffff',
  width: '48px',
  height: '48px',
  '&:hover': {
    backgroundColor: '#5a7054',
    transform: 'translateY(-2px)'
  },
  transition: 'all 0.3s ease'
}));

const BlogDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  // Update meta tags for SEO and sharing
  useMetaTags({
    title: post ? `${post.title} - EKKOFOOD Blog` : 'Blog - EKKOFOOD',
    description: post ? (post.excerpt || stripHtml(post.content || '').substring(0, 160) + '...') : 'Blog de agricultura ecológica y sostenible',
    image: post?.image || 'https://ekkofood.com/organic_farm.png',
    url: `https://ekkofood.com/${slug}`,
    type: 'article'
  });

  useEffect(() => {
    const loadPost = async () => {
      if (!slug) {
        setError('Slug del post no encontrado');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const wpPost = await fetchWordPressPostBySlug(slug);
        
        if (!wpPost) {
          // Post not found, set notFound flag to trigger redirect
          setNotFound(true);
          setLoading(false);
          return;
        }
        
        const transformedPost = transformWordPressPostToBlogPost(wpPost, []);
        setPost(transformedPost);
      } catch (err) {
        // If it's a 404 error or post not found, redirect to 404 page
        console.error('Error loading blog post:', err);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    loadPost();
  }, [slug]);

  const handleShare = async (platform: string) => {
    if (!post) return;

    try {
      // Explicitly construct current URL to ensure correct route sharing on Android
      const currentUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}${window.location.search}${window.location.hash}`;

      await shareUtil(platform, {
        title: post.title,
        text: post.excerpt || `Lee este artículo: ${post.title}`,
        url: currentUrl
      });
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  // Redirect to 404 page if post not found
  if (notFound) {
    return <Navigate to="/404-not-found" replace />;
  }

  if (loading) {
    return (
      <ThemeProvider theme={blogDetailTheme}>
        <BlogDetailContainer>
          <Container maxWidth="lg">
            <LoadingContainer>
              <CircularProgress size={60} sx={{ color: '#455b41' }} />
            </LoadingContainer>
          </Container>
        </BlogDetailContainer>
        <Footer />
      </ThemeProvider>
    );
  }

  if (error || !post) {
    return (
      <ThemeProvider theme={blogDetailTheme}>
        <BlogDetailContainer>
          <Container maxWidth="lg">
            <BackButton
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/noticias-blog')}
            >
              Volver al Blog
            </BackButton>
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {error || 'Artículo no encontrado'}
            </Alert>
          </Container>
        </BlogDetailContainer>
        <Footer />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={blogDetailTheme}>
      <PageHero
        title={stripHtml(post.title)}
        subtitle="Blog de Agricultura Sostenible"
        backgroundImage={post.image}
        showBreadcrumbs={true}
        breadcrumbs={[
          { label: 'Inicio', link: '/' },
          { label: 'Blog', link: '/noticias-blog' },
          { label: post.title.length > 50 ? post.title.substring(0, 50) + '...' : post.title, link: `/${post.slug}` }
        ]}
        height="500px"
        overlayOpacity={0.7}
      />

      <BlogDetailContainer>
        <Container maxWidth="md">
          <BackButton
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/noticias-blog')}
          >
            Volver al Blog
          </BackButton>

          <BlogHeader elevation={0}>
            <BlogTitle variant="h1">
              {post.title}
            </BlogTitle>
            
            <BlogMeta>
              <MetaItem>
                <PersonIcon />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {post.author}
                </Typography>
              </MetaItem>
              
              <MetaItem>
                <CalendarTodayIcon />
                <Typography variant="body2">
                  {post.date}
                </Typography>
              </MetaItem>
              
              <MetaItem>
                <AccessTimeIcon />
                <Typography variant="body2">
                  {post.readTime}
                </Typography>
              </MetaItem>
            </BlogMeta>
          </BlogHeader>

          <ContentContainer elevation={0}>
            <BlogContent
              dangerouslySetInnerHTML={{ __html: post.content || '' }}
            />
            
            {post.tags.length > 0 && (
              <>
                <Divider sx={{ my: 4 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Etiquetas:
                </Typography>
                <TagsContainer>
                  {post.tags.map((tag) => (
                    <TagChip key={tag} label={tag} size="medium" />
                  ))}
                </TagsContainer>
              </>
            )}

            <ShareContainer>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#455b41' }}>
                Compartir:
              </Typography>
              {/* Show native share button on mobile if supported */}
              {isMobileDevice() && isNativeShareSupported() ? (
                <ShareButton onClick={() => handleShare('native')} title="Compartir">
                  <ShareIcon />
                </ShareButton>
              ) : (
                <>
                  <ShareButton onClick={() => handleShare('facebook')} title="Compartir en Facebook">
                    <FacebookIcon />
                  </ShareButton>
                  <ShareButton onClick={() => handleShare('twitter')} title="Compartir en Twitter">
                    <TwitterIcon />
                  </ShareButton>
                  <ShareButton onClick={() => handleShare('linkedin')} title="Compartir en LinkedIn">
                    <LinkedInIcon />
                  </ShareButton>
                </>
              )}
            </ShareContainer>
          </ContentContainer>
        </Container>
      </BlogDetailContainer>

      <Footer />
    </ThemeProvider>
  );
};

export default BlogDetail;